import { GetCountries, GetState, GetCity } from 'react-country-state-city';
import { useState, useEffect } from 'react';

interface LocationSelectorProps {
  countryCode?: string | null;
  cityName?: string | null;
  onChange: (country: string | null, city: string | null) => void;
  placeholder?: string;
  required?: boolean;
}

export default function LocationSelector({
  countryCode,
  cityName,
  onChange,
  required = false
}: LocationSelectorProps) {
  const [countriesList, setCountriesList] = useState<any[]>([]);
  const [statesList, setStatesList] = useState<any[]>([]);
  const [citiesList, setCitiesList] = useState<any[]>([]);

  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [selectedCityName, setSelectedCityName] = useState<string>(cityName || '');

  // Load countries on mount
  useEffect(() => {
    GetCountries().then((result: any) => {
      setCountriesList(result);

      // If we have a countryCode, find and select it
      if (countryCode) {
        const country = result.find((c: any) => c.iso2 === countryCode);
        if (country) {
          setSelectedCountryId(country.id);
        }
      }
    });
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (selectedCountryId) {
      GetState(selectedCountryId).then((result: any) => {
        setStatesList(result);
      });
    } else {
      setStatesList([]);
      setCitiesList([]);
    }
  }, [selectedCountryId]);

  // Load cities when state changes
  useEffect(() => {
    if (selectedCountryId && selectedStateId) {
      GetCity(selectedCountryId, selectedStateId).then((result: any) => {
        setCitiesList(result);
      });
    } else {
      setCitiesList([]);
    }
  }, [selectedCountryId, selectedStateId]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedCountryId(countryId);
    setSelectedStateId(null);
    setSelectedCityName('');

    if (countryId) {
      const country = countriesList.find(c => c.id === countryId);
      onChange(country?.iso2 || null, null);
    } else {
      onChange(null, null);
    }
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedStateId(stateId);
    setSelectedCityName('');

    // Clear city when state changes
    const country = countriesList.find(c => c.id === selectedCountryId);
    onChange(country?.iso2 || null, null);
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityName = e.target.value;
    setSelectedCityName(cityName);

    const country = countriesList.find(c => c.id === selectedCountryId);
    onChange(country?.iso2 || null, cityName || null);
  };

  return (
    <div className="space-y-2">
      {/* Country Selector */}
      <select
        value={selectedCountryId || ''}
        onChange={handleCountryChange}
        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent focus:bg-white
          transition-all text-sm font-medium appearance-none"
        style={{ color: selectedCountryId ? '#111827' : '#9ca3af' }}
        required={required}
      >
        <option value="">Select Country</option>
        {countriesList.map((country) => (
          <option key={country.id} value={country.id}>
            {country.emoji} {country.name}
          </option>
        ))}
      </select>

      {/* State Selector */}
      {selectedCountryId && statesList.length > 0 && (
        <select
          value={selectedStateId || ''}
          onChange={handleStateChange}
          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900
            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent focus:bg-white
            transition-all text-sm font-medium appearance-none"
          style={{ color: selectedStateId ? '#111827' : '#9ca3af' }}
        >
          <option value="">Select State/Region</option>
          {statesList.map((state) => (
            <option key={state.id} value={state.id}>
              {state.name}
            </option>
          ))}
        </select>
      )}

      {/* City Selector */}
      {selectedStateId && citiesList.length > 0 && (
        <select
          value={selectedCityName}
          onChange={handleCityChange}
          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900
            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent focus:bg-white
            transition-all text-sm font-medium appearance-none"
          style={{ color: selectedCityName ? '#111827' : '#9ca3af' }}
        >
          <option value="">Select City</option>
          {citiesList.map((city) => (
            <option key={city.id} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
